import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import intl from "react-intl-universal";
import { TbChevronRight } from "react-icons/tb";
import BR from "country-flag-icons/react/1x1/BR";
import US from "country-flag-icons/react/1x1/US";
import PY from "country-flag-icons/react/1x1/PY";
import { CROSSBORDER_CUSTOMER_DETAIL_PATH, getCountry, MOCK_CUSTOMERS } from "../constants";
import { CrossborderKycStatusBadge } from "./CrossborderKycStatusBadge";

const FLAG_MAP = { br: BR, us: US, py: PY };

const GRID_COLS =
  "lg:grid-cols-[minmax(200px,2fr)_minmax(120px,1fr)_minmax(160px,1.2fr)_minmax(110px,0.9fr)_minmax(100px,0.7fr)]";

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return parts[0]?.substring(0, 2).toUpperCase() || "?";
};

const CustomerAvatar = ({ name }) => (
  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-cream text-xs font-bold text-[#9a7340] dark:bg-[#2a2a2a] dark:text-brand-gold-mid">
    {getInitials(name)}
  </div>
);

const CountryFlag = ({ countryCode }) => {
  const Flag = FLAG_MAP[countryCode];
  if (!Flag) return null;
  return <Flag className="h-4 w-4 shrink-0 rounded-full" title={countryCode?.toUpperCase()} />;
};

const CustomersHeader = () => (
  <div
    className={`hidden border-b border-gray-100 bg-gray-50/60 px-4 py-2.5 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 lg:grid ${GRID_COLS} lg:items-center lg:gap-4`}
  >
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
      {intl.get("crossborder.customers.columnName")}
    </span>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
      {intl.get("crossborder.customers.columnCountry")}
    </span>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
      {intl.get("crossborder.customers.columnEmail")}
    </span>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
      {intl.get("crossborder.customers.columnKyc")}
    </span>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
      {intl.get("crossborder.customers.columnCreated")}
    </span>
  </div>
);

const CustomerRow = ({ customer, onSelect }) => {
  const country = getCountry(customer.countryCode);

  const handleClick = () => onSelect?.(customer);
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect?.(customer);
    }
  };

  return (
  <>
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`hidden cursor-pointer border-b border-gray-100 px-4 py-3.5 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-brand-dark-border dark:hover:bg-brand-dark-elevated/50 lg:grid ${GRID_COLS} lg:items-center lg:gap-4`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <CustomerAvatar name={customer.label} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-brand-dark dark:text-gray-100">{customer.label}</p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">{customer.type}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <CountryFlag countryCode={customer.countryCode} />
        <span className="text-sm text-gray-600 dark:text-gray-300">{country?.label ?? "—"}</span>
      </div>
      <span className="truncate text-sm text-gray-600 dark:text-gray-300" title={customer.email}>
        {customer.email}
      </span>
      <CrossborderKycStatusBadge status={customer.kycStatus} />
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-gray-600 dark:text-gray-300">{customer.createdAt}</span>
        <TbChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
      </div>
    </div>

    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="cursor-pointer border-b border-gray-100 px-4 py-4 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-brand-dark-border dark:hover:bg-brand-dark-elevated/50 lg:hidden"
    >
      <div className="flex items-start gap-2.5">
        <CustomerAvatar name={customer.label} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">{customer.label}</p>
              <div className="mt-0.5 flex items-center gap-1.5">
                <CountryFlag countryCode={customer.countryCode} />
                <p className="text-xs text-gray-500 dark:text-gray-400">{country?.label}</p>
              </div>
            </div>
            <TbChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
          </div>
          <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">{customer.email}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <CrossborderKycStatusBadge status={customer.kycStatus} />
            <span className="text-xs text-gray-400">{customer.createdAt}</span>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export const CrossborderCustomersTable = ({ items = MOCK_CUSTOMERS }) => {
  const history = useHistory();

  const handleSelect = useMemo(
    () => (customer) => {
      history.push(CROSSBORDER_CUSTOMER_DETAIL_PATH.replace(":customerId", customer.id));
    },
    [history]
  );

  if (items.length === 0) {
    return (
      <div className="app-card overflow-hidden">
        <div className="app-empty-state m-4">{intl.get("crossborder.customers.empty")}</div>
      </div>
    );
  }

  return (
    <div className="app-card overflow-hidden">
      <CustomersHeader />
      <div>
        {items.map((customer) => (
          <CustomerRow key={customer.id} customer={customer} onSelect={handleSelect} />
        ))}
      </div>
    </div>
  );
};
